import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

// Cache to avoid repeated DB lookups
export interface AssessmentMeta {
  id: string;
  title: string;
  description: string | null;
  estimatedMinutes: number | null;
  sequenceOrder: number;
  levelSetContent: {
    title: string;
    description: string;
    sections: { icon: string; title: string; content: string }[];
    buttonText: string;
  } | null;
}

let assessmentCache: Record<string, AssessmentMeta> | null = null;

function parseLevelSetContent(raw: Json | null): AssessmentMeta['levelSetContent'] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  if (!obj.title || !obj.sections || !Array.isArray(obj.sections)) return null;
  return {
    title: String(obj.title || ''),
    description: String(obj.description || ''),
    sections: (obj.sections as any[]).map((s: any) => ({
      icon: String(s.icon || 'info'),
      title: String(s.title || ''),
      content: String(s.content || ''),
    })),
    buttonText: String(obj.buttonText || 'Continue to Assessment'),
  };
}

/**
 * Fetches the assessment UUID for a given module_id.
 * Uses the assessments table's module_id column.
 * Results are cached after first fetch.
 */
export async function getAssessmentIdByModuleId(moduleId: string): Promise<string | null> {
  if (!assessmentCache) {
    const { data, error } = await supabase
      .from('assessments')
      .select('id, module_id, assessment_name, description, estimated_duration_minutes, sequence_order, level_set_content');

    if (error || !data) {
      console.error('Failed to fetch assessments:', error);
      return null;
    }

    assessmentCache = {};
    for (const row of data) {
      const moduleKey = (row as any).module_id;
      if (moduleKey) {
        assessmentCache[moduleKey] = {
          id: row.id,
          title: row.assessment_name,
          description: row.description,
          estimatedMinutes: row.estimated_duration_minutes,
          sequenceOrder: row.sequence_order,
          levelSetContent: parseLevelSetContent(row.level_set_content),
        };
      }
    }
  }

  return assessmentCache[moduleId]?.id || null;
}

/**
 * Returns the full metadata for a given module_id (cached).
 */
export async function getAssessmentMetaByModuleId(moduleId: string): Promise<AssessmentMeta | null> {
  if (!assessmentCache) {
    await getAssessmentIdByModuleId(moduleId); // triggers cache load
  }
  return assessmentCache?.[moduleId] || null;
}

/**
 * Returns all assessment records (cached).
 */
export async function getAllAssessments(): Promise<Record<string, AssessmentMeta>> {
  if (!assessmentCache) {
    await getAssessmentIdByModuleId(''); // triggers cache load
  }
  return assessmentCache || {};
}

/** Clear cache (useful after data changes) */
export function clearAssessmentCache(): void {
  assessmentCache = null;
}
